import * as React from 'react';
import {
    List,
    Datagrid,
    TextField,
    TopToolbar,
    CreateButton,
    ExportButton,
    useRecordContext,
    FieldProps,
    Link,
    NumberField,
    useDataProvider,
    SelectInput,
    Filter,
    useGetOne,
    DataProviderContext,
    TextInput,
} from 'react-admin';
import ChaptersLinkField from './ChaptersLinkField';
import { useNavigate } from 'react-router-dom';
import { LoadingSection } from '../../common/LoadingSection';
import { useParams } from 'react-router';
import Button from '@mui/material/Button';
import { Edit, Visibility } from '@mui/icons-material';
import { Subjects } from '../../custom';
import FormattedDateTimeField from '../../common/FormattedDateTimeField';
import StatusTags from '../../common/StatusTags';
import ContentCounts from '../../common/ContentCounts';
import { STATUS } from '../../common/constants';
import { ChevronRightIcon } from 'lucide-react';
import ClearFiltersOnRouteChange from '../../common/ClearFiltersOnRouteChange';
import ConfirmDeleteButton from '../../common/ConfirmDeleteButton';
import { createCustomGoDataProvider } from '../../services';
import { NoDataSection } from '../../common/NoDataSection';
import ThumbnailField from '../../common/ThumbnailField';

const ListActions = ({ programId }: { programId: string }) => {
    const navigate = useNavigate();
    return (
        <TopToolbar>
            <CreateButton
                onClick={e => {
                    e.preventDefault();
                    navigate(`/programs/${programId}/subjects/create`);
                }}
            />
            <ExportButton />
        </TopToolbar>
    );
};

const EditButton = () => {
    const navigate = useNavigate();
    const record: Subjects = useRecordContext<Subjects>();
    let { programId } = useParams();
    return (
        <Button
            onClick={e => {
                navigate(`/programs/${programId}/subjects/${record.id}/edit`);
            }}
        >
            <Edit />
        </Button>
    );
};
const ShowButton = () => {
    const navigate = useNavigate();
    const record: Subjects = useRecordContext<Subjects>();
    let { programId } = useParams();
    return (
        <Button
            onClick={e => {
                navigate(`/programs/${programId}/subjects/${record.id}/show`);
            }}
        >
            <Visibility />
        </Button>
    );
};

const Actions = (props: FieldProps<Subjects>) => {
    let { programId } = useParams();
    return (
        <div className={'flex space-x-2'}>
            <ShowButton />
            <EditButton />
            <span onClick={e => e.stopPropagation()}>
                <ConfirmDeleteButton path={`programs/${programId}/subjects`} />
            </span>
        </div>
    );
};
Actions.defaultProps = {
    label: 'Actions',
};

const ListFilter = (props: any) => (
    <Filter {...props}>
        <TextInput label="Search" source="name" alwaysOn />
        <SelectInput source="status" label={'Status'} choices={STATUS} alwaysOn />
    </Filter>
);
const SubjectList = () => {
    const params = useParams();
    const navigate = useNavigate();
    const baseDataProvider = useDataProvider();
    if (!params['programId']) {
        return <LoadingSection />;
    }
    const customDataProvider = React.useMemo(
        () =>
            createCustomGoDataProvider(baseDataProvider, [
                { resource: 'subjects', apiEndpoint: '/subjects' },
            ]),
        [baseDataProvider],
    );
    return (
        <DataProviderContext.Provider value={customDataProvider}>
            <List
                title={<SubjectsTitle />}
                filters={<ListFilter />}
                empty={
                    <NoDataSection
                        resource={'subjects'}
                        onCreate={() => {
                            navigate(`/programs/${params['programId']}/subjects/create`);
                        }}
                    />
                }
                resource={'subjects'}
                actions={<ListActions programId={params['programId'] + ''} />}
                filter={{
                    programId: params['programId'],
                }}
                queryOptions={{
                    refetchOnWindowFocus: false,
                    refetchOnReconnect: false,
                }}
            >
                <ClearFiltersOnRouteChange />
                <Datagrid bulkActionButtons={false}>
                    <ThumbnailField source="image" sortable={false} />
                    <TextField source="name" sortable={false} />
                    <TextField source="slug" sortable={false} />
                    <StatusTags source="status" label={'Status'} sortable={false} />
                    <NumberField source="displayOrder" sortable />
                    <ContentCounts />
                    <FormattedDateTimeField source="createdAt" sortable />
                    <ChaptersLinkField />
                    <Actions />
                </Datagrid>
            </List>
        </DataProviderContext.Provider>
    );
};

const SubjectsTitle = () => {
    const { programId } = useParams();
    const { data: program, isLoading } = useGetOne('programs', { id: programId });

    if (isLoading) return <span>Loading...</span>;

    return (
        <span className="flex flex-row items-center text-lg font-semibold">
            <Link to={`/programs`} className="text-blue-600 hover:underline">
                {program?.name}
            </Link>
            <ChevronRightIcon />
            <span>Subjects</span>
        </span>
    );
};

export default SubjectList;
